import React from 'react';
import styled from 'styled-components';
import { animated, Spring } from 'react-spring/renderprops';
import { useStoreState } from 'easy-peasy';
import { translate, getQualificationLabel } from '~/utils';

const { highlightLabels } = config;

const ChartLabel = styled.text`
  font-size: 12px;
  font-weight: ${(p) => (p.strong ? 'bold' : 'normal')};
  pointer-events: none;
`;

const ChartLabelBg = styled(ChartLabel)`
  fill: #fff;
  stroke: #fff;
  stroke-width: ${(p) => (p.strong ? 4 : 2)};
  pointer-events: none;
`;

const Line = styled.line`
  stroke-width: 1;
  stroke: #000;
  shape-rendering: crispEdges;
  pointer-events: none;
`;

const getLabelAnchor = (x, xMax) => {
  if (x < 100) {
    return 'start';
  }

  if (x > xMax - 100) {
    return 'end';
  }

  return 'middle';
};

const LabelText = ({
  text,
  yOffset,
  strong,
  x,
  xMax,
  transform = translate(0, 0),
}) => {
  const textAnchor = getLabelAnchor(x, xMax);
  const xOffset = textAnchor === 'middle' ? 0 : textAnchor === 'start' ? -5 : 5;

  return (
    <g transform={transform}>
      <ChartLabelBg x={xOffset} y={yOffset - 3} textAnchor={textAnchor}>
        {text}
      </ChartLabelBg>
      <ChartLabel
        strong={strong}
        x={xOffset}
        y={yOffset - 3}
        textAnchor={textAnchor}
      >
        {text}
      </ChartLabel>
    </g>
  );
};

const Label = ({
  xScale,
  yScale,
  rScale,
  data,
  strong = false,
  animate = true,
}) => {
  if (!data) {
    return null;
  }

  const radius = rScale(data.r);
  const x = xScale(data.x);
  const y = yScale(data.y);
  const invertY = y < 100;
  const yOffset = invertY ? radius + 30 : -radius - 5;
  const qualification = getQualificationLabel(data.qualification);
  const lineY = invertY ? radius + 5 : yOffset;

  return (
    <Spring to={{ x, y }} immediate={!animate}>
      {(props) => (
        <animated.g transform={translate(props.x, props.y)}>
          <Line x1={0} x2={0} y1={lineY} y2={0} />
          <LabelText
            text={data.label}
            yOffset={yOffset}
            strong={strong}
            x={x}
            xMax={xScale.range()[1]}
            transform={translate(0, -12)}
          />
          <LabelText
            text={`(${qualification})`}
            yOffset={yOffset}
            strong={strong}
            x={x}
            xMax={xScale.range()[1]}
            transform={translate(0, 0)}
          />
        </animated.g>
      )}
    </Spring>
  );
};

const Annotations = ({ xScale, yScale, rScale, data }) => {
  const showDefaultLabels = useStoreState(
    (state) => state.app.showDefaultLabels
  );

  const tooltipData = useStoreState((state) => state.tooltip.data);

  const labels = showDefaultLabels
    ? data.filter((d) => highlightLabels.includes(d.value) && d.highlight)
    : [];
  const selectedLabel = data.find((d) => d.selected);
  const defaultAnnotations = labels.map((label) => (
    <Label
      key={label.value}
      xScale={xScale}
      yScale={yScale}
      rScale={rScale}
      data={label}
    />
  ));
  const selectedAnnotation = (
    <Label
      xScale={xScale}
      yScale={yScale}
      rScale={rScale}
      data={selectedLabel}
      strong
      animate={false}
      opacity={tooltipData ? 0.5 : 1}
    />
  );

  return (
    <g>
      {defaultAnnotations}
      {selectedAnnotation}
    </g>
  );
};

export default Annotations;
